import type { SvgIconComponent } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import FlipIcon from "@mui/icons-material/ThreeSixty";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "~contexts/I18nContext/I18nContext";
import { classList } from "~lib/classList";
import styles from "./TimeCard.module.scss";

type TimeValue = {
  hours: number;
  minutes: number;
  negative?: boolean;
};

type ScalarValue = {
  value: string;
  unit: string;
};

type TimeCardItem = ScalarValue & {
  label: string;
  tooltip?: ReactNode;
};

type ItemsValue = {
  caption: string;
  items: TimeCardItem[];
};

type MobileVariant = {
  name: "mobile";
};

type DesktopVariant = {
  name: "desktop";
  icon: SvgIconComponent;
};

type CommonProps = {
  label: string;
  tooltip?: ReactNode;
};

type MobileTimeCardProps = CommonProps & {
  variant?: MobileVariant;
  subdued?: boolean;
} & (TimeValue | ScalarValue);

type DesktopTimeCardProps = CommonProps & {
  variant: DesktopVariant;
} & (TimeValue | ScalarValue | ItemsValue);

export type TimeCardProps = MobileTimeCardProps | DesktopTimeCardProps;

export function TimeCard(props: TimeCardProps) {
  if (isDesktop(props)) {
    return <DesktopTimeCard {...props} />;
  }
  return <MobileTimeCard {...props} />;
}

function isDesktop(props: TimeCardProps): props is DesktopTimeCardProps {
  return props.variant?.name === "desktop";
}

function MobileTimeCard(props: MobileTimeCardProps) {
  const { label, tooltip, variant, subdued, ...rest } = props;
  return (
    <Paper className={classList(styles.mobileCard, subdued && styles.subdued)}>
      <span className={styles.label}>{label}</span>
      <TimeCardTime {...rest} />
      {tooltip && <TimeCardTooltip tooltip={tooltip} label={label} />}
    </Paper>
  );
}

function DesktopTimeCard(props: DesktopTimeCardProps) {
  const { label, tooltip, variant, ...rest } = props;
  const Icon = variant.icon;
  const [flipped, setFlipped] = useState(false);

  return (
    <div className={classList(styles.desktopCard, flipped && styles.flipped)}>
      <Paper className={styles.back}>
        <div className={styles.content}>
          {"items" in rest ? (
            <TimeCardItems {...rest} icon={Icon} />
          ) : (
            <>
              <Icon />
              <TimeCardTime {...rest} />
            </>
          )}
        </div>
        <div>
          {tooltip && <TimeCardTooltip tooltip={tooltip} />}
          <FlipIconButton flipped={flipped} onFlip={() => setFlipped(false)} />
        </div>
      </Paper>
      <Paper className={styles.front}>
        <div className={styles.content}>
          <Icon />
          <span className={styles.label}>{label}</span>
        </div>
        <FlipIconButton flipped={!flipped} onFlip={() => setFlipped(true)} />
      </Paper>
    </div>
  );
}

function FlipIconButton({
  flipped,
  onFlip,
}: {
  flipped: boolean;
  onFlip: () => void;
}) {
  const t = useTranslation();
  return (
    <IconButton
      className={styles.flipIcon}
      aria-label={flipped ? t.revealLabel : t.revealValue}
      aria-expanded={flipped}
      size="small"
      onClick={onFlip}
    >
      <FlipIcon />
    </IconButton>
  );
}

function TimeCardTime(props: TimeValue | ScalarValue) {
  return (
    <span className={styles.time}>
      {"hours" in props ? (
        <>
          {props.negative && <span>-</span>}
          <span>{props.hours}</span> <span className={styles.unit}>h</span>{" "}
          <span>{props.minutes}</span> <span className={styles.unit}>min</span>
        </>
      ) : (
        <>
          <span>{props.value}</span>{" "}
          <span className={styles.unit}>{props.unit}</span>
        </>
      )}
    </span>
  );
}

function TimeCardItems({
  caption,
  items,
  icon: Icon,
}: ItemsValue & { icon: SvgIconComponent }) {
  return (
    <div className={styles.itemsBlock}>
      <div className={styles.caption}>
        <Icon fontSize="inherit" />
        <span>{caption}</span>
      </div>
      <dl className={styles.items}>
        {items.map(({ label, value, unit, tooltip }) => (
          <div key={label} className={styles.item}>
            <dt className={styles.itemLabel}>{label}</dt>
            <dd className={styles.itemValue}>
              <span className={styles.time}>{value}</span>{" "}
              <span className={styles.unit}>{unit}</span>
              {tooltip && (
                <TimeCardTooltip
                  tooltip={tooltip}
                  className={styles.itemInfoIcon}
                  label={label}
                />
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TimeCardTooltip({
  tooltip,
  className = styles.infoIcon,
  label,
}: {
  tooltip: ReactNode;
  className?: string;
  label?: string;
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const t = useTranslation();
  return (
    <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
      <Tooltip
        open={tooltipOpen}
        title={tooltip}
        placement="bottom-end"
        onClose={() => setTooltipOpen(false)}
        disableHoverListener
        disableFocusListener
        disableTouchListener
      >
        <IconButton
          className={className}
          aria-label={
            label ? `${t.moreInformationAbout} ${label}` : t.moreInformation
          }
          size="small"
          onClick={() => setTooltipOpen((v) => !v)}
        >
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
}
